import { FC, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { BackIcon } from 'assets/icons';
import CustomButton, { ButtonType } from 'components/custom-button/CustomButton';
import routesPath from 'routes/RoutesPath';
import CustomInput from 'components/custom-input.tsx/CustomInput';
import { useAppSelector } from 'hooks';
import { selectCommissionBaseMap, selectCurrentTeam } from 'containers/app/appSlice';
import { CommissionType } from '../commission-settings/constants';
import CommissionRate from '../components/CommissionRate';
import {
  useCreateExternalCompanyMutation,
  useLazyGetExternalCompanyByIdQuery,
  useUpdateExternalCompanyMutation
} from './api';
import Loader from 'components/loader/Loader';
import { REGEX } from 'constants/common';
import { isEmailEndingValid } from 'utils/commonUtils';
import { SettingsCleverTapEvents } from '../events';
import { logAnalyticEvent } from 'utils/analytics';
import { getPhoneNumberDigits, isValidPhoneNumber } from 'utils/phoneNumberUtils';
import { CommissionBaseItem } from 'containers/app/app.model';

const sections = [
  { id: 'company-details', label: 'Company details' },
  { id: 'contract', label: 'Contract' }
];

const AddEditCompanyProfile: FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const isEditMode = location.pathname.includes('edit');
  const currentTeam = useAppSelector(selectCurrentTeam);
  const commissionBaseMap = useAppSelector(selectCommissionBaseMap);

  const [createExternalCompany, { isLoading: isLoadingCreateCompany }] = useCreateExternalCompanyMutation();
  const [updateExternalCompany, { isLoading: isLoadingUpdateCompany }] = useUpdateExternalCompanyMutation();
  const [getExternalCompanyById, getState] = useLazyGetExternalCompanyByIdQuery();
  const { isLoading: isLoadingCompany, isUninitialized: isUninitializedCompanies, data: companyData } = getState;
  // Form state
  const [companyName, setCompanyName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [commissionRateData, setCommissionRateData] = useState({
    value: '',
    unit: CommissionType.ABSOLUTE,
    baseType: null
  });

  // Validation states
  const [errors, setErrors] = useState({
    companyName: '',
    firstName: false,
    lastName: false,
    email: false,
    phone: false,
    commissionRate: false,
    commissionBase: false
  });

  // Add activeSection state
  const [activeSection, setActiveSection] = useState(sections[0].id);

  // Add scroll handler
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  const validateForm = () => {
    const newErrors = {
      companyName: companyName.trim() ? '' : 'Company name is required',
      firstName: false,
      lastName: false,
      email: email.trim() ? !REGEX.email.test(email) || !isEmailEndingValid(email) : false,
      phone: phone.trim() ? !isValidPhoneNumber(getPhoneNumberDigits(phone)) : false,
      commissionRate: false,
      commissionBase:
        commissionRateData.value && commissionRateData.unit === CommissionType.PERCENTAGE
          ? !commissionRateData.baseType
          : false
    };

    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const payload = {
      teamId: currentTeam?.id?.toString() || '',
      companyName,
      companyPocFirstname: firstName,
      companyPocLastname: lastName,
      companyPocEmail: email,
      companyPocPhone: phone ? getPhoneNumberDigits(phone) : '',
      commissionType: commissionRateData.value ? (commissionRateData.unit as CommissionType) : null,
      commissionValue: parseFloat(commissionRateData.value),
      commissionApplyOn: commissionRateData.baseType?.key || ''
    };

    try {
      if (isEditMode && id) {
        await updateExternalCompany({
          ...payload,
          externalCompanyId: id,
          status: companyData?.status
        }).unwrap();
        logAnalyticEvent(SettingsCleverTapEvents.webSettingsExternalCompanyEdited);
      } else {
        await createExternalCompany(payload).unwrap();
      }
      if (location.state?.returnTo) {
        navigate(location.state.returnTo, {
          state: { showExternalCompanyModal: true },
          replace: true
        });
        return;
      }
      navigate(routesPath.EXTERNAL_COMPANY_SETTINGS, { replace: true });
    } catch (error) {
      // Error handling
    }
  };

  const handleCommissionRateChange = (data: { value: string; unit: string; baseType?: CommissionBaseItem }) => {
    setCommissionRateData({
      value: data.value,
      unit: data.unit as CommissionType,
      baseType: data.baseType || null
    });
    if (data.unit === CommissionType.ABSOLUTE) {
      setErrors(prev => ({ ...prev, commissionBase: false }));
    }
  };

  const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event?.target?.value;
    if (REGEX.phoneNumberInput.test(value)) {
      setPhone(value);
      setErrors(prev => ({ ...prev, phone: false }));
    }
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    setErrors(prev => ({ ...prev, email: false }));
  };

  // Fetch company data if in edit mode
  useEffect(() => {
    const fetchCompanyData = async () => {
      if (isEditMode && id && currentTeam?.id) {
        try {
          const response = await getExternalCompanyById({
            teamId: currentTeam.id.toString(),
            externalCompanyId: id
          }).unwrap();

          // Populate form with company data
          setCompanyName(response.companyName);
          setFirstName(response.companyPocFirstname);
          setLastName(response.companyPocLastname);
          setEmail(response.companyPocEmail);
          setPhone(response.companyPocPhone);
          setCommissionRateData({
            value: response.commissionValue.toString(),
            unit: response?.commissionType ? (response.commissionType as CommissionType) : CommissionType.ABSOLUTE,
            baseType: response.commissionApplyOn ? commissionBaseMap[response.commissionApplyOn] : null
          });
        } catch (error) {
          // Error handling
        }
      }
    };

    fetchCompanyData();
  }, [isEditMode, id, currentTeam?.id]);

  const getSectionBorderClass = (sectionId: string) => {
    if (sectionId === 'company-details' && (errors.companyName || errors.phone || errors.email)) return 'border-error';
    if (sectionId === 'contract' && (errors.commissionRate || errors.commissionBase)) return 'border-error';
    if (sectionId === activeSection) return 'border-secondary';
    return 'border-borderGray';
  };

  const onBackClick = () => {
    if (location.state?.returnTo) {
      navigate(location.state.returnTo, {
        state: { showExternalCompanyModal: true }
      });
    } else {
      navigate(-1);
    }
  };

  const handleNameChange = (value: string) => {
    setCompanyName(value);
    setErrors(prev => ({ ...prev, companyName: '' }));
  };

  if (isEditMode && (isLoadingCompany || isUninitializedCompanies)) {
    return <Loader />;
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="sticky top-0 z-10 flex w-full border-b border-borderGray bg-white">
        <div className="flex h-[72px] items-center border-borderGray px-6">
          <BackIcon className="mr-5 cursor-pointer" onClick={onBackClick} />
          <span className="text-xl font-bold text-primaryText">
            {isEditMode ? 'Edit company profile' : 'Add company profile'}
          </span>
        </div>
        <div className="flex h-[72px] flex-1 items-center justify-end px-[30px]">
          <CustomButton
            id="save-button"
            type={ButtonType.PRIMARY}
            childrenContainerStyles="text-17px font-semibold"
            className="h-10"
            disabled={isLoadingCreateCompany || isLoadingUpdateCompany}
            onClick={handleSubmit}>
            {isEditMode ? 'Save' : 'Create'}
          </CustomButton>
        </div>
      </div>

      {/* Content */}
      <div className="flex h-[calc(100vh-72px)] overflow-hidden">
        {/* Navigation */}
        <div className="sticky top-[72px] h-full min-w-[262px] bg-white">
          <div className="flex h-full flex-col gap-4 overflow-y-auto p-4 px-12">
            {sections.map(section => (
              <div
                key={section.id}
                className={`flex h-[49px] cursor-pointer items-center rounded-md px-4 ${
                  activeSection === section.id ? 'border border-secondary text-secondary' : 'text-gray-600'
                }`}
                onClick={() => scrollToSection(section.id)}>
                {section.label}
              </div>
            ))}
          </div>
        </div>

        {/* Form Content */}
        <div className="h-full w-full flex-1">
          <div className="customNormalScroll ml-[60px] flex h-full overflow-y-auto 2xl:ml-[120px]">
            <div className="min-w-[961px] space-y-12 p-6 pb-24 text-headingGray">
              {/* Company Details Section */}
              <section id="company-details">
                <h2 className="mb-2 text-xl font-bold">Company details</h2>
                <div
                  className={`space-y-6 rounded-md border p-5 text-headingGray ${getSectionBorderClass(
                    'company-details'
                  )}`}
                  onClick={() => setActiveSection('company-details')}>
                  {/* Company Name */}
                  <div>
                    <label className="mb-2 block text-17px font-semibold">
                      Company name<span className="text-red-500">*</span>
                    </label>
                    <CustomInput
                      id="company-name"
                      value={companyName}
                      onChange={handleNameChange}
                      placeholder="Enter company name"
                      error={errors.companyName}
                      className="text-17px font-normal text-primaryText outline-none"
                      maxLength={30}
                    />
                  </div>

                  {/* Primary Contact */}
                  <div>
                    <label className="mb-2 block text-17px font-semibold">Primary contact</label>
                    <div className="grid grid-cols-2 gap-4">
                      <CustomInput
                        id="first-name"
                        value={firstName}
                        onChange={value => setFirstName(value)}
                        placeholder="First name"
                        error={errors.firstName ? 'First name is required' : ''}
                        className="text-17px font-normal text-primaryText outline-none"
                      />
                      <CustomInput
                        id="last-name"
                        value={lastName}
                        onChange={value => setLastName(value)}
                        placeholder="Last name"
                        error={errors.lastName ? 'Last name is required' : ''}
                        className="text-17px font-normal text-primaryText outline-none"
                      />
                    </div>
                  </div>
                  {/* Contact Information */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="mb-2 block text-17px font-semibold">Phone number</label>
                      <div
                        className="flex h-[50px] w-full appearance-none flex-row items-center rounded bg-secondaryBtn 
                      py-3 px-4 leading-tight">
                        <div className={'mr-1 text-17px text-accentText'}>+1</div>
                        <input
                          type="text"
                          id="company-phone"
                          placeholder="(000) 00-0000"
                          className={
                            // eslint-disable-next-line max-len
                            'appearance-none bg-secondaryBtn text-17px font-normal text-primaryText outline-none focus:outline-none'
                          }
                          value={phone}
                          onChange={handlePhoneChange}
                        />
                      </div>
                      {errors.phone && (
                        <div className="mt-1 text-sbase font-semibold text-error">
                          Please enter a valid phone number
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="mb-2 block text-17px font-semibold">Email Id</label>
                      <CustomInput
                        id="email"
                        value={email}
                        onChange={handleEmailChange}
                        placeholder="Enter email address"
                        error={errors.email ? 'Please enter a valid email address' : ''}
                        className="text-17px font-normal text-primaryText outline-none"
                      />
                    </div>
                  </div>
                </div>
              </section>

              {/* Contract Section */}
              <section id="contract">
                <h2 className="mb-2 text-xl font-bold">Contract</h2>
                <div
                  className={`rounded-md border p-5 ${getSectionBorderClass('contract')}`}
                  onClick={() => setActiveSection('contract')}>
                  <div className="mb-2 text-17px font-semibold text-headingGray">Default Referral rate</div>
                  <CommissionRate
                    onChange={handleCommissionRateChange}
                    initialData={commissionRateData}
                    isExternalCompany={true}
                  />
                  {errors.commissionBase && (
                    <div className="mt-1 text-sbase font-semibold text-error">Please select a valid base</div>
                  )}
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddEditCompanyProfile;
